/* eslint-disable @typescript-eslint/no-explicit-any */
import { moveDataValue, removeDataValue, setDataValue, splitPathIntoParts } from '@juvare/json-data-util';
import React from 'react';
/**
 * Handles editor actions such as drag and drop, edit, delete, etc.
 * This context is required for most components in this library.
 *
 * @see EditorContextType
 * @see useEditorContext
 */
export const WidgetEditorContext = React.createContext(createEditorContext({
    editMode: false,
    rootElement: { current: null },
    dragTargets: [],
    nextId: 1,
}, () => undefined, () => undefined, () => undefined));
/**
 * Hook that create the WidgetEditorContext for a given root element.
 *
 * @param editMode - toggles between preview and edit mode
 * @param rootElement - root element of the editor area
 * @param initialRootConfig - initial data for the editor
 * @param widgets - list of available widgets, this will be passed to createWidget function
 * @param onChange - called when changes to config are made
 */
export function useWidgetEditorContext(editMode, rootElement, initialRootConfig, onChange, widgets) {
    const [rootConfig, setRootConfig] = React.useState(initialRootConfig);
    const [widgetEditorContext, setWidgetEditorContext] = React.useState();
    React.useEffect(() => {
        setWidgetEditorContext((prevWidgetEditorContext) => {
            var _a;
            return createEditorContext({
                editMode: editMode,
                rootElement: rootElement,
                dragTargets: [],
                nextId: (_a = prevWidgetEditorContext === null || prevWidgetEditorContext === void 0 ? void 0 : prevWidgetEditorContext.state.nextId) !== null && _a !== void 0 ? _a : 1,
            }, setWidgetEditorContext, setRootConfig, onChange !== null && onChange !== void 0 ? onChange : (() => undefined), widgets);
        });
    }, [editMode, rootElement, widgets, onChange]);
    React.useEffect(() => {
        const unselectWidget = () => {
            if (!(widgetEditorContext === null || widgetEditorContext === void 0 ? void 0 : widgetEditorContext.state.selectedWidgetLockedForEdit) && (widgetEditorContext === null || widgetEditorContext === void 0 ? void 0 : widgetEditorContext.getSelectedWidgetId())) {
                if (widgetEditorContext.state.selectedWidgetElement) {
                    widgetEditorContext.state.selectedWidgetElement.blur();
                }
                widgetEditorContext.state.selectedWidgetId = undefined;
                widgetEditorContext.state.selectedWidgetElement = undefined;
                widgetEditorContext.state.selectedWidgetLockedForEdit = false;
                setWidgetEditorContext(createEditorContext(widgetEditorContext.state, setWidgetEditorContext, setRootConfig, onChange !== null && onChange !== void 0 ? onChange : (() => undefined), widgets));
            }
        };
        const handleDocumentPointerDown = () => unselectWidget();
        const handleDocumentKeyDown = (event) => {
            switch (event.key) {
                case 'Escape':
                    unselectWidget();
                    break;
            }
        };
        document.addEventListener('pointerdown', handleDocumentPointerDown);
        document.addEventListener('keydown', handleDocumentKeyDown);
        return () => {
            document.removeEventListener('pointerdown', handleDocumentPointerDown);
            document.removeEventListener('keydown', handleDocumentKeyDown);
        };
    }, [rootElement, widgets, onChange, widgetEditorContext]);
    return [rootConfig, setRootConfig, widgetEditorContext];
}
function createEditorContext(state, setEditorContext, setRootConfig, onChange, widgets) {
    var _a;
    // intentionally mutating editorState object to prevent re-renders when dragging a widget
    const getInternalId = (widgetConfig) => {
        if (!widgetConfig['_internal_id_']) {
            widgetConfig['_internal_id_'] = '_editor_' + state.nextId++;
        }
        return widgetConfig['_internal_id_'];
    };
    return {
        state: state,
        editMode: state.editMode,
        rootElement: (_a = state.rootElement.current) !== null && _a !== void 0 ? _a : undefined,
        getInternalId: getInternalId,
        getSelectedWidgetId: () => state.selectedWidgetId,
        getSelectedWidgetElement: () => state.selectedWidgetElement,
        isSelectedWidgetLockedForEdit: () => { var _a; return (_a = state.selectedWidgetLockedForEdit) !== null && _a !== void 0 ? _a : false; },
        lockWidgetForEdit: () => {
            state.selectedWidgetLockedForEdit = true;
        },
        unlockWidgetForEdit: () => {
            state.selectedWidgetLockedForEdit = false;
            if (state.selectedWidgetElement) {
                state.selectedWidgetElement.focus();
            }
        },
        calculateEditorPath: (element) => {
            if (state.rootElement.current) {
                const editorPath = calculateEditorPath(state.rootElement.current, element);
                return editorPath !== '' ? editorPath : undefined;
            }
            else {
                return undefined;
            }
        },
        onDragBegin: (element, moveElement, moveEditorPath) => {
            state.dragTargets = collectDragTargets(state.rootElement.current, moveEditorPath);
            state.dropElement = element;
            state.moveElement = moveElement;
            state.moveEditorPath = moveEditorPath;
            state.matchedDragTarget = undefined;
            if (state.selectedWidgetId && !moveElement) {
                state.selectedWidgetId = undefined;
                setEditorContext(createEditorContext(state, setEditorContext, setRootConfig, onChange, widgets));
            }
        },
        onDragEnd: (createWidget) => {
            const selection = document.getSelection();
            if (selection) {
                selection.removeAllRanges();
            }
            const dropElement = state.dropElement;
            const moveEditorPath = state.moveEditorPath;
            const matchedDragTarget = state.matchedDragTarget;
            state.dragTargets = [];
            state.dropElement = undefined;
            state.moveElement = undefined;
            state.moveEditorPath = undefined;
            state.matchedDragTarget = undefined;
            if (matchedDragTarget) {
                removeDropElement(matchedDragTarget, dropElement);
                const editorPath = matchedDragTarget.editorPath;
                if (moveEditorPath) {
                    setRootConfig((prevRootConfig) => {
                        const newRootConfig = JSON.parse(JSON.stringify(prevRootConfig));
                        const movedWidget = moveDataValue(newRootConfig, moveEditorPath, editorPath);
                        if (typeof movedWidget === 'object' && !Array.isArray(movedWidget)) {
                            const id = getInternalId(movedWidget);
                            setEditorContext(createEditorContext(Object.assign(Object.assign({}, state), { selectedWidgetId: id }), setEditorContext, setRootConfig, onChange, widgets));
                            // try to focus the selected element after it is rendered
                            window.setTimeout(() => {
                                const element = findWidgetElementById(state.rootElement.current, id);
                                if (element) {
                                    element.focus();
                                }
                            }, 0);
                            onChange(newRootConfig);
                            return newRootConfig;
                        }
                        else {
                            return prevRootConfig;
                        }
                    });
                }
                else {
                    setRootConfig((prevRootConfig) => {
                        const newRootConfig = JSON.parse(JSON.stringify(prevRootConfig));
                        const id = '_editor_' + state.nextId++;
                        const createdWidget = createWidget(newRootConfig, editorPath, widgets);
                        if (typeof createdWidget === 'object' && !Array.isArray(createdWidget)) {
                            getInternalId(createdWidget); // sets new unique internal id
                            createdWidget['_internal_new_'] = true;
                            setDataValue(newRootConfig, editorPath, createdWidget);
                            setEditorContext(createEditorContext(Object.assign(Object.assign({}, state), { selectedWidgetId: id }), setEditorContext, setRootConfig, onChange, widgets));
                            // try to focus the selected element after it is rendered
                            window.setTimeout(() => {
                                const element = findWidgetElementById(state.rootElement.current, id);
                                if (element) {
                                    element.focus();
                                }
                            }, 0);
                            onChange(newRootConfig);
                            return newRootConfig;
                        }
                        else {
                            return prevRootConfig;
                        }
                    });
                }
            }
        },
        onDrag: (x, y) => {
            const closestDragTarget = findClosestDragTarget(state.dragTargets, x, y, state.moveElement);
            if (state.matchedDragTarget && closestDragTarget && state.matchedDragTarget != closestDragTarget) {
                removeDropElement(state.matchedDragTarget, state.dropElement);
                appendDropElement(closestDragTarget, state.dropElement);
                state.matchedDragTarget = closestDragTarget;
            }
            else if (state.matchedDragTarget && !closestDragTarget) {
                removeDropElement(state.matchedDragTarget, state.dropElement);
                state.matchedDragTarget = undefined;
            }
            else if (!state.matchedDragTarget && closestDragTarget) {
                appendDropElement(closestDragTarget, state.dropElement);
                state.matchedDragTarget = closestDragTarget;
            }
        },
        selectWidget: (id, element) => {
            if (state.selectedWidgetId !== id || state.selectedWidgetElement !== element) {
                state.selectedWidgetId = id;
                state.selectedWidgetElement = element;
                state.selectedWidgetLockedForEdit = false;
                setEditorContext(createEditorContext(state, setEditorContext, setRootConfig, onChange, widgets));
            }
        },
        updateWidget: (editorPath, widgetConfig) => {
            if (widgetConfig != null) {
                setRootConfig((prevRootConfig) => {
                    const newRootConfig = JSON.parse(JSON.stringify(prevRootConfig));
                    if (setDataValue(newRootConfig, editorPath, widgetConfig) != null) {
                        setEditorContext(createEditorContext(Object.assign({}, state), setEditorContext, setRootConfig, onChange, widgets));
                        onChange(newRootConfig);
                        return newRootConfig;
                    }
                    else {
                        return prevRootConfig;
                    }
                });
            }
        },
        deleteWidget: (editorPath) => {
            setRootConfig((orevRootConfig) => {
                const newRootConfig = JSON.parse(JSON.stringify(orevRootConfig));
                if (removeDataValue(newRootConfig, editorPath) != null) {
                    setEditorContext(createEditorContext(Object.assign(Object.assign({}, state), { selectedWidgetId: undefined }), setEditorContext, setRootConfig, onChange, widgets));
                    onChange(newRootConfig);
                    return newRootConfig;
                }
                else {
                    return orevRootConfig;
                }
            });
        },
    };
}
function collectDragTargets(editorRootDiv, moveEditorPath) {
    if (editorRootDiv) {
        const dragTargets = [];
        const moveEditorPathList = moveEditorPath === null || moveEditorPath === void 0 ? void 0 : moveEditorPath.split('.');
        let itselfEditorPath;
        const skippedEditorPaths = [];
        if (moveEditorPathList && moveEditorPathList[moveEditorPathList.length - 1].startsWith('#')) {
            const parentPathList = moveEditorPathList.slice(0, -1);
            const index = parseInt(moveEditorPathList[moveEditorPathList.length - 1].substring(1));
            itselfEditorPath = [...parentPathList, '#' + index].join('.');
            skippedEditorPaths.push(itselfEditorPath); // cannot set to itself
            skippedEditorPaths.push([...parentPathList, '#' + index + '+'].join('.')); // cannot insert to itself
            skippedEditorPaths.push([...parentPathList, '#' + (index + 1) + '+'].join('.')); // cannot insert after itself because it would result in no change at all
        }
        editorRootDiv.querySelectorAll('[data-drag-target=true]').forEach((element) => {
            const htmlElement = element;
            const editorPath = calculateEditorPath(editorRootDiv, htmlElement);
            if (moveEditorPath) {
                if (itselfEditorPath && editorPath.startsWith(itselfEditorPath + '.')) {
                    // cannot move to itself
                    return;
                }
                if (skippedEditorPaths.includes(editorPath)) {
                    // elements:  [   a   b   c   ]
                    // positions: [ 0 0 1   2   3 ]
                    // cannot move element a from position 0 to position 1 because it would result in no changes at all
                    return;
                }
            }
            dragTargets.push({
                element: htmlElement,
                editorPath: editorPath,
            });
        });
        return dragTargets;
    }
    else {
        return [];
    }
}
function findWidgetElementById(editorRootDiv, id) {
    var _a;
    if (editorRootDiv) {
        return ((_a = editorRootDiv.querySelector(`[data-editor-widget-id="${id}"]`)) !== null && _a !== void 0 ? _a : undefined);
    }
    else {
        return undefined;
    }
}
function calculateEditorPath(editorRootDiv, element) {
    var _a;
    let editorPath = (_a = element.dataset.editorPath) !== null && _a !== void 0 ? _a : '';
    let parent = element.parentElement;
    while (parent && parent !== editorRootDiv) {
        if (parent.dataset.editorPath) {
            if (editorPath !== '') {
                editorPath = parent.dataset.editorPath + '.' + editorPath;
            }
            else {
                editorPath = parent.dataset.editorPath;
            }
        }
        parent = parent.parentElement;
    }
    return splitPathIntoParts(editorPath).join('.');
}
function findClosestDragTarget(dragTargets, pointerX, pointerY, moveElement) {
    let minDistanceToDragTarget = moveElement ? calculateDistanceToDragTarget(moveElement, pointerX, pointerY) : 1000;
    if (minDistanceToDragTarget < 0) {
        return undefined;
    }
    let closedDragTarget = undefined;
    for (const dragTarget of dragTargets) {
        const d = calculateDistanceToDragTarget(dragTarget.element, pointerX, pointerY);
        if (d < 0) {
            return dragTarget;
        }
        else if (d <= 80 && d < minDistanceToDragTarget) {
            minDistanceToDragTarget = d;
            closedDragTarget = dragTarget;
        }
    }
    return closedDragTarget;
}
function calculateDistanceToDragTarget(dragElement, pointerX, pointerY) {
    const rect = dragElement.getBoundingClientRect();
    const dragTargetX = rect.x + window.scrollX;
    const dragTargetY = rect.y + window.scrollY;
    const dragTargetWidth = rect.width;
    const dragTargetHeight = rect.height;
    //
    //     corner 1           match column           corner 2
    //
    //                   +---------------------+
    //                   |                     |
    //     match row     |     drag target     |     match row
    //                   |                     |
    //                   +---------------------+
    //
    //     corner 3           match column           corner 4
    //
    const matchColumn = pointerX >= dragTargetX && pointerX < dragTargetX + dragTargetWidth;
    const matchRow = pointerY >= dragTargetY && pointerY < dragTargetY + dragTargetHeight;
    if (matchColumn && matchRow) {
        // must be inside drag target
        return -1;
    }
    else if (matchColumn) {
        // must be inside column
        if (pointerY < dragTargetY) {
            // must be above
            return dragTargetY - pointerY;
        }
        else {
            // must be below
            return pointerY - (dragTargetY + dragTargetHeight);
        }
    }
    else if (matchRow) {
        // must be inside row
        if (pointerX < dragTargetX) {
            // must be to the left
            return dragTargetX - pointerX;
        }
        else {
            // must be to the right
            return pointerX - (dragTargetX + dragTargetWidth);
        }
    }
    else {
        // must be in one of the 4 corners
        // will use Pythagorean theorem to calculate distance to the edge of the corner
        let a;
        let b;
        const left = pointerX < dragTargetX;
        const top = pointerY < dragTargetY;
        if (left && top) {
            // must be in corner 1
            a = dragTargetX - pointerX;
            b = dragTargetY - pointerY;
        }
        else if (top) {
            // must be in corner 2
            a = pointerX - (dragTargetX + dragTargetWidth);
            b = dragTargetY - pointerY;
        }
        else if (left) {
            // must be in corner 3
            a = dragTargetX - pointerX;
            b = pointerY - (dragTargetY + dragTargetHeight);
        }
        else {
            // must be in corner 4
            a = pointerX - (dragTargetX + dragTargetWidth);
            b = pointerY - (dragTargetY + dragTargetHeight);
        }
        return Math.sqrt(a * a + b * b);
    }
}
function appendDropElement(dragTarget, dropElement) {
    if (dropElement) {
        const container = dragTarget.element.querySelector('[data-drag-target-container=true]');
        if (container) {
            container.appendChild(dropElement);
            container.style.display = 'block';
            const placeholder = dragTarget.element.querySelector('[data-drag-target-placeholder=true]');
            if (placeholder) {
                placeholder.style.display = 'none';
            }
        }
        else {
            dragTarget.element.appendChild(dropElement);
        }
        dropElement.style.display = 'block';
    }
}
function removeDropElement(dragTarget, dropElement) {
    if (dropElement) {
        const container = dragTarget.element.querySelector('[data-drag-target-container=true]');
        if (container) {
            safeRemoveChild(container, dropElement);
            container.style.display = 'none';
            const placeholder = dragTarget.element.querySelector('[data-drag-target-placeholder=true]');
            if (placeholder) {
                placeholder.style.display = 'block';
            }
        }
        else {
            safeRemoveChild(dragTarget.element, dropElement);
        }
        dropElement.style.display = 'none';
    }
}
function safeRemoveChild(parent, child) {
    if (child.parentElement === parent) {
        parent.removeChild(child);
    }
}
